import { Trans } from "@lingui/macro";
import {
  IApiEpBillingPaymentProvider,
  IApiEpBillingProduct,
  IApiEpBillingProductPurchaseRes,
} from "@smp/api";
import { UiBox, UiButton } from "@smp/ui";
import { testAppendAttr } from "@smp/utils";
import { useFormik } from "formik";
import { FC, useState } from "react";
import { useUpdateEffect } from "react-use";

import { useMutationBillingProductBill } from "@/queries/billing";

import { InternalAccount } from "./components/internal_account";
import { OrganizationInvoice } from "./components/organization_invoice";
import { PaymentProviderSelector } from "./components/payment_provider_selector";
import { PaymentTypeSelector } from "./components/payment_type_selector";
import { PlanPeriodSelector } from "./components/plan_period_selector";
import { ProductDescription } from "./components/product_description";
import { Warnings } from "./components/warnings";
import classes from "./invoice_form.module.css";
import { IInvoiceFormValues, useInvoiceForm } from "./use_invoice_form";

export interface IInvoiceFormProps {
  initialProduct: IApiEpBillingProduct;
  onCreateInvoice: (data: {
    invoice: IApiEpBillingProductPurchaseRes;
    paymentProvider: IApiEpBillingPaymentProvider;
  }) => void;
  productName?: string;
  onChangeProduct?: (product: IApiEpBillingProduct) => void;
}

export const InvoiceForm: FC<IInvoiceFormProps> = ({
  initialProduct,
  onCreateInvoice,
  onChangeProduct,
  productName,
}) => {
  const [monthlyPrice, setMonthlyPrice] = useState<number>();

  const { initialValues, isPaymentSelectorAllowed } =
    useInvoiceForm(initialProduct);

  const { mutate: createInvoice, isLoading: isInvoiceCreating } =
    useMutationBillingProductBill(true, {
      onSuccess: (invoice, { paymentProvider }) => {
        onCreateInvoice({
          invoice,
          paymentProvider,
        });
      },
    });

  const { values, setFieldValue, submitForm } = useFormik<IInvoiceFormValues>({
    initialValues: initialValues,
    onSubmit: ({
      product,
      paymentProvider,
      paymentType,
      isInternalAccountUse,
    }) => {
      if (!paymentProvider) {
        return;
      }

      createInvoice({
        product,
        paymentProvider,
        paymentType,
        isInternalAccountUse,
      });
    },
  });

  useUpdateEffect(() => {
    if (onChangeProduct) {
      onChangeProduct(values.product);
    }
  }, [values.product]);

  return (
    <div className={classes.grid}>
      <div className={classes.grid__col}>
        {values.product.type === "plan-product" && values.paymentType && (
          <div className={classes.grid__row}>
            <PlanPeriodSelector
              paymentType={values.paymentType}
              defaultValue={
                initialValues.product.type === "plan-product"
                  ? initialValues.product
                  : undefined
              }
              value={values.product}
              onChange={(value) => setFieldValue("product", value)}
              onChangeMonthlyPrice={setMonthlyPrice}
            />
          </div>
        )}

        {isPaymentSelectorAllowed && (
          <div className={classes.grid__row}>
            <PaymentTypeSelector
              value={values.paymentType}
              onChange={(value) => setFieldValue("paymentType", value)}
            />
          </div>
        )}

        <div className={classes.grid__row}>
          <OrganizationInvoice />
        </div>

        <div className={classes.grid__row}>
          <PaymentProviderSelector
            paymentType={values.paymentType}
            product={values.product}
            value={values.paymentProvider}
            onChange={(value) => setFieldValue("paymentProvider", value)}
          />

          <InternalAccount
            paymentType={values.paymentType}
            product={values.product}
            checked={values.isInternalAccountUse}
            onChange={(checked) =>
              setFieldValue("isInternalAccountUse", checked)
            }
          />
        </div>

        <Warnings product={values.product} paymentType={values.paymentType} />
      </div>

      <div className={classes.grid__col}>
        <ProductDescription
          product={values.product}
          paymentType={values.paymentType}
          monthlyPrice={monthlyPrice}
          productName={productName}
        >
          <UiBox>
            <UiButton
              {...testAppendAttr("id", `purchase_go_to_payment`)}
              color="primary"
              fullWidth
              isLoading={isInvoiceCreating}
              onClick={submitForm}
              variant="contained"
            >
              <Trans>Go to the payment</Trans>
            </UiButton>
          </UiBox>
        </ProductDescription>
      </div>
    </div>
  );
};
